import { client } from '@api/client';

import { normalizeObject } from '@utils/common';

import { TIdName, TNameTitle } from '@interfaces/common/common';
import { IItemsResponse } from '@interfaces/common/response';
import {
	IAssetRentRequest,
	IAssetRentRequestCardApproveItem,
	IAssetRentRequestCardItem,
	IAssetRentRequestDocument,
	IRentedOutAssetDates,
} from '@interfaces/rent/assetRent';
import {
	IAddItemToExistingAssetRentRequestDTO,
	IApprovalActionAssetByUnitDTO,
	IAssetsRequestsDTO,
	IGetApprovalInfoDTO,
	IGetAssetRentCardItemsDTO,
	IProposeDatesForAssetRentItemDTO,
	ISendActionWithCommentDTO,
	IStopRentingAssetDTO,
} from '@interfaces/rent/dto/assetRentDTO';

import { TAssetAcquiredStatuses } from '@enums/asset';

import { ERoles } from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/constants';

import { URL } from './urls';

const {
	getAssetsRentOutgoingRequests,
	getAssetsRentIncomingRequests,
	getAssetsRequestStatuses,
	getAssetRent,
	addNewAssetToExistedRequest,
	getAssetRentRequestCard,
	declineRequest,
	proposeNewDates,
} = URL;

export const getAssetRentOutgoingRequests = ({
	page,
	pageSize,
	searchValue,
	sortBy,
	assetOwnerId,
	networkOperatorId,
	requestedEndDate,
	requestedStartDate,
	requestStatus,
	requestType,
	filterParams,
	dateFilterParams,
}: IAssetsRequestsDTO): Promise<IItemsResponse<IAssetRentRequestDocument>> => {
	return client.get(getAssetsRentOutgoingRequests, {
		params: {
			page,
			pageSize,
			searchValue,
			sortDirection: sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id?.split('.')[0],
			assetOwnerId,
			networkOperatorId,
			requestedEndDate,
			requestedStartDate,
			requestStatus,
			requestType,
			...filterParams,
			...dateFilterParams,
		},
	});
};

export const getAssetRentIncomingRequests = ({
	page,
	pageSize,
	searchValue,
	assetOwnerId,
	networkOperatorId,
	requestedEndDate,
	requestedStartDate,
	requestStatus,
	requestType,
	filterParams,
	sortBy,
}: IAssetsRequestsDTO): Promise<IItemsResponse<IAssetRentRequestDocument>> => {
	return client.get(getAssetsRentIncomingRequests, {
		params: {
			page,
			pageSize,
			searchValue,
			sortDirection: sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id?.split('.')[0],
			assetOwnerId,
			networkOperatorId,
			requestedEndDate,
			requestedStartDate,
			requestStatus,
			requestType,
			...filterParams,
		},
	});
};

export const getAssetRentCard = (id: string): Promise<IAssetRentRequest> => {
	return client.get(`${getAssetRentRequestCard}/${id}`);
};

export const getAssetRentCardItems = ({
	id,
	params,
	role,
}: IGetAssetRentCardItemsDTO): Promise<IItemsResponse<IAssetRentRequestCardItem>> => {
	const {
		endRentDate,
		startRentDate,
		page = 1,
		pageSize = 10,
		periodAvailability,
		searchValue,
		sortDirection,
		sortFieldName,
		statusAssetOwner,
		statusNetworkOperator,
		typeId,
		approveStatus,
	} = params;

	return client.get(`${getAssetRentRequestCard}/${id}/items/${role === ERoles.ao ? 'as-ao' : 'as-mno'}`, {
		params: {
			page,
			pageSize,
			sortDirection,
			searchValue,
			startRentDate,
			endRentDate,
			periodAvailability,
			sortFieldName,
			statusAssetOwner,
			statusNetworkOperator,
			typeId,
			approveStatus,
		},
	});
};

export const getAssetRequestStatuses = (): Promise<TNameTitle[]> => {
	return client.get(getAssetsRequestStatuses);
};

export const addItemToExistingAssetRentRequest = ({
	assetDbId,
	startDate,
	endDate,
	requestDbId,
	comment,
}: IAddItemToExistingAssetRentRequestDTO): Promise<void> => {
	return client.post(addNewAssetToExistedRequest, normalizeObject({
		assetDbId,
		startDate,
		endDate,
		requestDbId,
		comment,
		isSendReason: !!comment?.length,
	}));
};

export const declineRentRequest = (id: string, comment?: string): Promise<void> => {
	return client.post(`${declineRequest}/${id}/decline`, {
		comment,
		isSendReason: !!comment,
	});
};

export const acceptOneRentAsset = (id: string): Promise<void> => {
	return client.post(`${getAssetRent}/card/items/${id}/accept-proposed`, {});
};

export const declineOneRentAsset = (id: string): Promise<void> => {
	return client.post(`${declineRequest}/items/${id}/decline-proposed`, {});
};

export const removeProposedAsset = (id: string): Promise<void> => {
	return client.delete(`${declineRequest}/items/${id}/remove-proposed`);
};

export const proposeDates = ({
	requestDbId,
	itemId,
	startDate,
	endDate,
	comment,
}: IProposeDatesForAssetRentItemDTO): Promise<void> => {
	return client.post(proposeNewDates, {
		itemId,
		startDate,
		endDate,
		requestDbId,
		isSendReason: !!comment?.length,
		comment,
	});
};

export const getApprovalInfoAsAO = ({
	itemId,
}: IGetApprovalInfoDTO): Promise<IAssetRentRequestCardApproveItem[]> => {
	return client.get(`${URL.getApprovalInfoAsAO}`, {
		params: {
			itemId,
		},
	});
};

export const getApprovalInfoAsMNO = ({
	itemId,
}: IGetApprovalInfoDTO): Promise<IAssetRentRequestCardApproveItem[]> => {
	return client.get(URL.getApprovalInfoAsMNO, {
		params: {
			itemId,
		},
	});
};

export const getApprovalInfo = (
	isAo: boolean, data: IGetApprovalInfoDTO,
): Promise<IAssetRentRequestCardApproveItem[]> => {
	return isAo ? getApprovalInfoAsAO(data) : getApprovalInfoAsMNO(data);
};

export const sendRequestItemToAO = ({ requestId, comment }: ISendActionWithCommentDTO): Promise<void> => {
	return client.post(`${URL.sendRequestToAO}${requestId}/send-to-revision`, {
		comment,
	});
};

export const sendRequestItemToMNO = ({ requestId, comment }: ISendActionWithCommentDTO): Promise<void> => {
	return client.post(`${URL.sendRequestToAO}${requestId}/send-for-acceptance`, {
		comment,
	});
};

export const acceptAssetByUnit = ({
	approvalUnitId,
	comment,
	itemId,
}: IApprovalActionAssetByUnitDTO): Promise<void> => {
	return client.post(URL.acceptItemByApprovalUnit, undefined, {
		params: normalizeObject({
			approvalUnitId,
			comment,
			itemId,
		}),
	});
};

export const declineAssetByUnit = ({
	approvalUnitId,
	comment,
	itemId,
}: IApprovalActionAssetByUnitDTO): Promise<void> => {
	return client.post(URL.declineItemByApprovalUnit, undefined, {
		params: normalizeObject({
			approvalUnitId,
			comment,
			itemId,
		}),
	});
};

export const revokeAssetByUnit = ({
	approvalUnitId,
	comment,
	itemId,
}: IApprovalActionAssetByUnitDTO): Promise<void> => {
	return client.post(URL.revokeItemByApprovalUnit, undefined, {
		params: normalizeObject({
			approvalUnitId,
			comment,
			itemId,
		}),
	});
};

export const acceptAssetRentRequestAsAO = (id: string, comment: string): Promise<void> => {
	return client.post(`${URL.acceptRequest}${id}/accept-as-ao`, {
		id,
		comment,
		isSendReason: !!comment,
	});
};

export const acceptAssetRentRequestAsMNO = (id: string, comment: string): Promise<void> => {
	return client.post(`${URL.acceptRequest}${id}/accept-as-mno`, {
		id,
		comment,
		isSendReason: !!comment,
	});
};

export const contractAssetRentRequest = (id: string, comment: string): Promise<void> => {
	return client.post(`${URL.contractRequest}${id}/contract`, {
		id,
		comment,
		isSendReason: !!comment,
	});
};

export const doneAssetRentRequest = (id: string, comment: string): Promise<void> => {
	return client.post(`${URL.contractRequest}${id}/done`, undefined, {
		params: {
			id,
			comment,
			isSendReason: !!comment,
		},
	});
};

export const getPickedDates = (assetDbId: string, requestId: string): Promise<IRentedOutAssetDates> => {
	return client.get(`${URL.alreadyRentedDates}${assetDbId}/${requestId}/dates-picked`);
};

export const stopRentingAsset = ({
	itemId,
	from,
}: IStopRentingAssetDTO): Promise<void> => {
	return client.post(`assets/${itemId}/rent/stop`, { from });
};

export const getRentedAssetIdsByType = (type: TAssetAcquiredStatuses) => {
	return client.get(URL.findRentedAssets, {
		params: {
			type,
		},
	});
};

export const getAssetOwners = (): Promise<TIdName> => client(URL.getAssetOwners);

export const getNetworkOperators = (): Promise<TIdName> => client(URL.getNetworkOperators);

